<template>
  <v-card
    class="d-flex flex-column"
    style="height: calc(100vh - 80px); overflow: hidden"
  >
    <!-- Filtros arriba -->
    <div class="search-container">
      <v-text-field
        v-model="searchApodo"
        label="Buscar Apodo"
        single-line
        hide-details
        dense
        outlined
      ></v-text-field>
      <v-text-field
        v-model="searchDispositivo"
        label="Buscar Dispositivo"
        single-line
        hide-details
        dense
        outlined
      ></v-text-field>
      <v-select
        v-model="searchActivado"
        :items="['Activado', 'Desactivado']"
        label="Filtrar Activado"
        dense
        outlined
        clearable
        hide-details
      ></v-select>
      <v-select
        v-model="searchBateria"
        :items="['Alta', 'Media', 'Baja']"
        label="Filtrar Batería"
        dense
        outlined
        clearable
        hide-details
      ></v-select>
      <v-select
        v-model="searchTipo"
        :items="['iBeacon', 'SmartBle', 'Reader']"
        label="Filtrar Tipo"
        dense
        outlined
        clearable
        hide-details
      ></v-select>
      <v-select
        v-model="searchPosibleEstado"
        :items="[
          'Ubicado',
          'Posible tránsito',
          'Posible extravío',
          'Necesita revisar',
          'Operativo',
          'Sin registro',
        ]"
        label="Filtrar Posible Estado"
        dense
        outlined
        clearable
        hide-details
      ></v-select>
      <v-date-input
        v-model="fechaInicio"
        label="Fecha desde"
        clearable
        prepend-icon="mdi-calendar-start"
        density="compact"
        hide-details
        @click:clear="
          () => {
            fechaInicio = null;
          }
        "
      />

      <v-date-input
        v-model="fechaFin"
        label="Fecha hasta"
        clearable
        prepend-icon="mdi-calendar-end"
        density="compact"
        hide-details
        @click:clear="
          () => {
            fechaFin = null;
          }
        "
      />
      <v-btn
        color="primary"
        dark
        small
        class="mt-2"
        @click="clearFilters"
        :disabled="!hasActiveFilters"
      >
        <v-icon left>mdi-filter-remove</v-icon>
        Limpiar Filtros
      </v-btn>
    </div>

    <!-- Barra de progreso -->
    <v-progress-linear
      v-if="loading"
      indeterminate
      color="primary"
    ></v-progress-linear>

    <!-- Tabla de datos -->
    <v-data-table
      v-else
      :headers="headers"
      :items="filteredData"
      class="elevation-1"
      v-model:items-per-page="itemsPerPage"
      :page="page"
      :items-length="filteredData.length"
      hide-default-footer
      fixed-header
    >
      <template #top>
        <v-toolbar flat>
          <v-toolbar-title>Inventario de Dispositivos</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            dark
            @click="reloadData"
            small
            style="margin-right: 8px"
          >
            <v-icon left>mdi-refresh</v-icon>
            Recargar
          </v-btn>
        </v-toolbar>
      </template>
      <template #no-data>
        <v-alert type="info" class="ma-4" border="left" colored-border>
          <div class="d-flex flex-column align-center">
            <div class="mb-2">
              <v-icon left>mdi-magnify-close</v-icon>
              No se encontraron dispositivos con esos filtros
            </div>
            <v-btn color="primary" small @click="clearFilters">
              <v-icon left>mdi-filter-remove</v-icon>
              Limpiar Filtros
            </v-btn>
          </div>
        </v-alert>
      </template>
      <!-- Apodo editable -->
      <template v-slot:[`item.Apodo`]="{ item }">
        <div class="d-flex align-center">
          <span>{{ item.Apodo }}</span>
          <v-icon
            small
            class="ml-2"
            color="primary"
            style="cursor: pointer"
            @click="openEditApodo(item)"
          >
            mdi-pencil
          </v-icon>
          <v-icon
            small
            class="ml-2"
            color="info"
            style="cursor: pointer"
            @click="openDeviceDetails(item)"
          >
            mdi-information
          </v-icon>
        </div>
      </template>

      <template v-slot:[`item.TipoDispositivo`]="{ item }">
        <v-chip
          :color="getTipoColor(item.TipoDispositivo)"
          dark
          small
          class="d-flex align-center"
        >
          <v-icon start size="16" class="mr-1">
            {{ getTipoIcon(item.TipoDispositivo) }}
          </v-icon>
          {{ item.TipoDispositivo }}
        </v-chip>
      </template>

      <!-- Icono Activado con toggle -->
      <template v-slot:[`item.Activado`]="{ item }">
        <div
          class="d-flex align-center"
          @click="toggleActivado(item)"
          style="cursor: pointer"
        >
          <v-icon
            :color="item.Activado === 'Activado' ? 'green' : 'red'"
            class="mr-1"
          >
            {{ item.Activado === "Activado" ? "mdi-power" : "mdi-power-off" }}
          </v-icon>
          <span>{{ item.Activado === "Activado" ? "ON" : "OFF" }}</span>
        </div>
      </template>

      <!-- Último lector -->
      <template v-slot:[`item.ApodoLector`]="{ item }">
        <span>{{ item.ApodoLector || "No visto" }}</span>
      </template>

      <!-- % Batería -->
      <template v-slot:[`item.Bateria`]="{ item }">
        <v-progress-linear
          v-if="item.Bateria !== null"
          :model-value="item.Bateria"
          height="16"
          :color="item.Bateria >= 99.5 ? 'lime' : batteryColor(item.Bateria)"
          rounded
          striped
          style="
            transition: width 2s ease-in-out, background-color 0.5s ease-in-out;
          "
        >
          <template v-slot:default>
            <small>{{ parseFloat(item.Bateria).toFixed(2) + "%" }}</small>
          </template>
        </v-progress-linear>
        <div v-else class="text-center text-caption">Sin datos</div>
      </template>

      <template v-slot:[`item.UltimoRegistro`]="{ item }">
        <div class="d-flex flex-column">
          <div class="d-flex align-center">
            <v-icon
              v-if="isOldUpdate(item.UltimoRegistro)"
              color="red"
              class="mr-1"
              size="16"
            >
              mdi-alert-circle
            </v-icon>
            <span>{{ formatDate(item.UltimoRegistro) }}</span>
          </div>
          <small class="text-grey">
            {{ getRelativeTime(item.UltimoRegistro) }}
          </small>
        </div>
      </template>

      <!-- Estado de impacto / movimiento / parado -->
      <template v-slot:[`item.Estado`]="{ item }">
        <span v-if="item.Estado === 'Impacto'">
          <v-icon color="red" class="mr-1">mdi-alert</v-icon>
          Impacto
        </span>
        <span v-else-if="item.Estado === 'En movimiento'">
          <v-icon color="blue" class="mr-1">mdi-run</v-icon>
          En movimiento
        </span>
        <span v-else-if="item.Estado === 'Parado'">
          <v-icon color="grey" class="mr-1">mdi-stop-circle</v-icon>
          Parado
        </span>
        <span v-else>
          {{ item.Estado || "—" }}
        </span>
      </template>

      <!-- Posible Estado -->
      <template v-slot:[`item.PosibleEstado`]="{ item }">
        <v-chip
          :color="getEstadoColor(item.PosibleEstado)"
          dark
          small
          class="d-flex align-center"
        >
          <v-icon start size="16" class="mr-1">
            {{ getEstadoIcon(item.PosibleEstado) }}
          </v-icon>
          {{ item.PosibleEstado }}
        </v-chip>
      </template>

      <!-- Fecha de Matriculación -->
      <template v-slot:[`item.FechaRegistro`]="{ item }">
        {{
          item.FechaRegistro
            ? dayjs(item.FechaRegistro).isValid()
              ? dayjs(item.FechaRegistro).format("DD/MM/YYYY HH:mm:ss")
              : "-"
            : "-"
        }}
      </template>

      <!-- Footer personalizado -->
      <template #bottom>
        <div
          class="d-flex w-100 pa-2 align-center justify-space-between flex-wrap"
        >
          <div class="d-flex align-center flex-wrap">
            <v-select
              v-model="itemsPerPage"
              :items="[10, 20, 50, 100]"
              label="Filas por página"
              dense
              outlined
              hide-details
              style="max-width: 150px"
              class="mr-4"
            ></v-select>
            <v-pagination
              v-model="page"
              :length="Math.ceil(filteredData.length / itemsPerPage)"
              total-visible="2"
              class="mr-4"
            ></v-pagination>
            <span>Total: {{ filteredData.length }}</span>
          </div>
          <div class="d-flex">
            <v-btn
              color="info"
              dark
              @click="statsDialog = true"
              small
              style="margin-right: 8px"
            >
              <v-icon left>mdi-chart-donut</v-icon>
              Ver Resumen
            </v-btn>
            <v-btn color="success" dark @click="exportarExcel" small>
              <v-icon left>mdi-file-excel</v-icon>
              Exportar a Excel
            </v-btn>
            <v-btn
              color="red"
              dark
              @click="exportarPDF"
              small
              style="margin-left: 8px"
            >
              <v-icon left>mdi-file-pdf-box</v-icon>
              Exportar a PDF
            </v-btn>
          </div>
        </div>
      </template>
    </v-data-table>

    <v-alert v-if="error" type="error" dismissible>
      {{ error }}
    </v-alert>
    <v-snackbar v-model="snackbar" :timeout="3000" color="success">
      {{ snackbarText }}
    </v-snackbar>

    <!-- Dialogo para editar Apodo -->
    <v-dialog v-model="editDialog" max-width="500px">
      <v-card>
        <v-card-title>Editar Apodo</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="editedApodo"
            label="Nuevo Apodo"
            dense
            outlined
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="saveApodo">Guardar</v-btn>
          <v-btn color="grey" text @click="editDialog = false">Cancelar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="detailsDialog" max-width="500px">
      <v-card>
        <v-card-title>Detalles del dispositivo</v-card-title>
        <v-card-text>
          <div>
            <strong>Tipo Dispositivo:</strong>
            {{ selectedDevice?.TipoDispositivo }}
          </div>
          <div>
            <strong>Último Registro:</strong>
            {{ formatDate(selectedDevice?.UltimoRegistro) }}
          </div>
          <div>
            <strong>Actualizado:</strong>
            {{ getRelativeTime(selectedDevice?.UltimoRegistro) }}
          </div>
          <div>
            <strong>Batería:</strong> {{ selectedDevice?.Bateria?.toFixed(2) }}%
          </div>
          <div><strong>Latitud:</strong> {{ selectedDevice?.Lat }}</div>
          <div><strong>Longitud:</strong> {{ selectedDevice?.Lon }}</div>
          <div>
            <strong>Visto por:</strong>
            {{ selectedDevice?.ApodoLector || "No visto" }}
          </div>
          <div
            id="map"
            style="height: 200px; width: 100%; margin-top: 10px"
          ></div>
          <div
            v-if="!selectedDevice?.Lat || !selectedDevice?.Lon"
            class="text-red mt-2"
          >
            <v-icon color="red" class="mr-1">mdi-map-marker-off</v-icon>
            Sin ubicación disponible
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="detailsDialog = false"
            >Cerrar</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="statsDialog" max-width="500px" persistent>
      <v-card>
        <v-card-title class="headline"> Estado de Dispositivos </v-card-title>
        <v-card-text>
          <v-list dense>
            <v-list-item>
              <v-list-item-icon
                ><v-icon color="primary">mdi-counter</v-icon></v-list-item-icon
              >
              <v-list-item-title
                >Total dispositivos:
                <strong>{{ totalDevices }}</strong></v-list-item-title
              >
            </v-list-item>
            <v-list-item>
              <v-list-item-icon
                ><v-icon color="green">mdi-power</v-icon></v-list-item-icon
              >
              <v-list-item-title
                >Activados:
                <strong>{{ activatedDevices }}</strong></v-list-item-title
              >
            </v-list-item>
            <v-list-item>
              <v-list-item-icon
                ><v-icon color="red">mdi-power-off</v-icon></v-list-item-icon
              >
              <v-list-item-title
                >Desactivados:
                <strong>{{ deactivatedDevices }}</strong></v-list-item-title
              >
            </v-list-item>
            <v-list-item>
              <v-list-item-icon
                ><v-icon color="orange"
                  >mdi-clock-alert</v-icon
                ></v-list-item-icon
              >
              <v-list-item-title
                >Sin actualizar > 3 días:
                <strong>{{ outdatedDevices }}</strong></v-list-item-title
              >
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="statsDialog = false"
            >Cerrar</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script setup>
import { ref, onMounted, computed, watch } from "vue";
import { usePocStore } from "@/stores/pocStore";
import api from "@/services/api";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import dayjs from "dayjs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import relativeTime from "dayjs/plugin/relativeTime";
import { useRoute } from "vue-router";

const route = useRoute();

dayjs.extend(relativeTime);

const statsDialog = ref(false);
const totalDevices = ref(0);
const activatedDevices = ref(0);
const deactivatedDevices = ref(0);
const outdatedDevices = ref(0);

const customIcon = new L.Icon({
  iconUrl: "https://cdn-icons-png.flaticon.com/512/484/484167.png",
  iconSize: [32, 32], // Tamaño del icono
  iconAnchor: [16, 32], // Punto donde "ancla" el icono
  popupAnchor: [0, -32], // Dónde se abre el popup relativo al icono
});

const snackbar = ref(false);
const snackbarText = ref("");

const pocStore = usePocStore();

// Estados para la tabla
const page = ref(1);
const itemsPerPage = ref(10);

// Filtros
const searchDispositivo = ref("");
const searchApodo = ref("");
const searchActivado = ref("");
const searchBateria = ref("");
const searchTipo = ref("");
const searchPosibleEstado = ref("");
const fechaInicio = ref(null);
const fechaFin = ref(null);

// Estados para editar Apodo
const editDialog = ref(false);
const editedApodo = ref("");
const editedDevice = ref(null);

const detailsDialog = ref(false);
const selectedDevice = ref(null);

// Tabla base
const headers = [
  { title: "Apodo", value: "Apodo", sortable: true },
  { title: "Dispositivo", value: "Dispositivo", sortable: true },
  { title: "Tipo", value: "TipoDispositivo", sortable: true },
  { title: "Activado", value: "Activado", sortable: true },
  { title: "Último Registro", value: "UltimoRegistro", sortable: true },
  { title: "Visto por", value: "ApodoLector", sortable: true },
  { title: "% Batería", value: "Bateria", sortable: true },
  { title: "Posible Estado", value: "PosibleEstado", sortable: true },
  { title: "Estado", value: "Estado", sortable: true },
  { title: "Fecha de Matriculación", value: "FechaRegistro", sortable: true },
];

// Filtros reactivos
const hasActiveFilters = computed(() => {
  return (
    searchDispositivo.value ||
    searchApodo.value ||
    searchActivado.value ||
    searchBateria.value ||
    searchTipo.value ||
    searchPosibleEstado.value ||
    fechaInicio.value ||
    fechaFin.value
  );
});

// Computed con filtros aplicados
const filteredData = computed(() => {
  return pocStore.data.filter((item) => {
    const matchesDispositivo = item.Dispositivo?.toLowerCase().includes(
      searchDispositivo.value.toLowerCase()
    );
    const matchesApodo = item.Apodo?.toLowerCase().includes(
      searchApodo.value.toLowerCase()
    );
    const matchesActivado = searchActivado.value
      ? item.Activado === searchActivado.value
      : true;
    const matchesTipo = searchTipo.value
      ? item.TipoDispositivo === searchTipo.value
      : true;
    const matchesEstado = searchPosibleEstado.value
      ? item.PosibleEstado === searchPosibleEstado.value
      : true;
    let matchesBateria = true;
    if (searchBateria.value === "Alta") {
      matchesBateria = item.Bateria >= 50;
    } else if (searchBateria.value === "Media") {
      matchesBateria = item.Bateria >= 25 && item.Bateria < 50;
    } else if (searchBateria.value === "Baja") {
      matchesBateria = item.Bateria < 25;
    }

    const matchesFechaRegistro = (() => {
      const start = fechaInicio.value;
      const end = fechaFin.value;
      const fecha = item.FechaRegistro;

      if (!start && !end) return true;

      if (start && !end) {
        return dayjs(fecha).isAfter(
          dayjs(start).startOf("day").subtract(1, "second")
        );
      }

      if (!start && end) {
        return dayjs(fecha).isBefore(dayjs(end).endOf("day").add(1, "second"));
      }

      // si hay ambos
      return (
        dayjs(fecha).isAfter(
          dayjs(start).startOf("day").subtract(1, "second")
        ) && dayjs(fecha).isBefore(dayjs(end).endOf("day").add(1, "second"))
      );
    })();

    return (
      matchesDispositivo &&
      matchesApodo &&
      matchesActivado &&
      matchesTipo &&
      matchesBateria &&
      matchesEstado &&
      matchesFechaRegistro
    );
  });
});

// Datos loading/error
const loading = computed(() => pocStore.loading);
const error = computed(() => pocStore.error);

// Recarga de datos
async function reloadData() {
  await pocStore.fetchPocData();
  calculateStats();
  snackbarText.value = "Datos recargados";
  snackbar.value = true;
}

// Quita al final "+HH:MM", "-HH:MM" o "Z"
function stripOffset(value) {
  return value ? value.replace(/([+-]\d{2}:\d{2}|Z)$/, "") : value;
}

// Actualizar estadísticas al cargar datos
function calculateStats() {
  const now = dayjs();
  totalDevices.value = pocStore.data.length;
  activatedDevices.value = pocStore.data.filter(
    (d) => d.Activado === "Activado"
  ).length;
  deactivatedDevices.value = pocStore.data.filter(
    (d) => d.Activado === "Desactivado"
  ).length;
  outdatedDevices.value = pocStore.data.filter((d) => {
    if (!d.UltimoRegistro) return true;
    const lastSeen = dayjs(d.UltimoRegistro);
    return now.diff(lastSeen, "day") >= 3;
  }).length;
}

// Formatear tiempo relativo
function getRelativeTime(value) {
  if (!value || value === "Sin determinar") return "Sin datos";
  const clean = stripOffset(value);
  const d = dayjs(clean);
  return d.isValid() ? d.fromNow() : "Sin datos";
}

// Comprobar si el último registro es antiguo
function isOldUpdate(ultimoRegistro) {
  if (!ultimoRegistro || ultimoRegistro === "Sin determinar") return true;
  const now = dayjs();
  const lastUpdate = dayjs(ultimoRegistro);
  return now.diff(lastUpdate, "minute") > 300; // Más de 60 minutos
}

function getEstadoColor(estado) {
  switch (estado) {
    case "Ubicado":
      return "green";
    case "Posible tránsito":
      return "orange";
    case "Posible extravío":
      return "red";
    case "Necesita revisar":
      return "red lighten-2";
    case "Operativo":
      return "blue";
    case "Sin registro":
      return "grey";
    default:
      return "grey";
  }
}

function getEstadoIcon(estado) {
  switch (estado) {
    case "Ubicado":
      return "mdi-check-circle";
    case "Posible tránsito":
      return "mdi-map-marker-path";
    case "Posible extravío":
      return "mdi-alert-circle";
    case "Necesita revisar":
      return "mdi-wrench";
    case "Operativo":
      return "mdi-power-plug";
    case "Sin registro":
      return "mdi-clock-off";
    default:
      return "mdi-help-circle";
  }
}

// Obtener color según el tipo de dispositivo
function getTipoColor(tipo) {
  if (tipo === "Reader") return "blue";
  if (tipo === "iBeacon") return "green";
  if (tipo === "SmartBle") return "orange";
  return "grey"; // por si acaso
}

// Obtener icono según el tipo de dispositivo
function getTipoIcon(tipo) {
  if (tipo === "Reader") return "mdi-access-point-network"; // Icono red/lector
  if (tipo === "iBeacon") return "mdi-bluetooth"; // Icono bluetooth
  if (tipo === "SmartBle") return "mdi-bluetooth-connect"; // Icono bluetooth conectado
  return "mdi-help-circle"; // Icono por defecto
}

// Exportar a Excel
const exportarExcel = async () => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Status POC");
  worksheet.columns = headers.map((header) => ({
    header: header.title,
    key: header.value,
    width: 20,
  }));

  filteredData.value.forEach((item) => {
    const row = {};
    headers.forEach((header) => {
      row[header.value] = item[header.value];
    });
    worksheet.addRow(row);
  });

  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(new Blob([buffer]), "status_poc.xlsx");
};

// Exportar a PDF
const exportarPDF = () => {
  const doc = new jsPDF("p", "mm", "a4");

  // Título
  doc.setFontSize(20);
  doc.setTextColor(40, 40, 40);
  doc.text("Inventario dispositivos", 14, 20);

  // Subtítulo
  doc.setFontSize(10);
  doc.setTextColor(100);
  doc.text(`Exportado el: ${dayjs().format("DD/MM/YYYY HH:mm:ss")}`, 14, 27);

  const columns = headers.map((header) => ({
    header: header.title,
    dataKey: header.value,
  }));

  // 🛠️ FORMATEAMOS LOS DATOS COMO LA TABLA
  const rows = filteredData.value.map((item) => ({
    Apodo: item.Apodo,
    Dispositivo: item.Dispositivo,
    Activado: item.Activado === "Activado" ? "ON" : "OFF",
    UltimoRegistro: formatDate(item.UltimoRegistro),
    ApodoLector: item.ApodoLector || "No visto",
    TieneBateria: batteryIcon(item.Bateria).replace("mdi-", ""), // Icono batería como texto
    Bateria:
      item.Bateria !== null ? item.Bateria.toFixed(2) + "%" : "Sin datos",
    Roll: formatAngle(item.Roll),
    Pitch: formatAngle(item.Pitch),
    Lat: item.Lat,
    Lon: item.Lon,
  }));

  autoTable(doc, {
    startY: 35,
    head: [columns.map((col) => col.header)],
    body: rows.map((row) => columns.map((col) => row[col.dataKey])),
    theme: "striped",
    headStyles: { fillColor: [22, 160, 133] }, // Verde bonito
    alternateRowStyles: { fillColor: [245, 245, 245] }, // Gris claro
    styles: { fontSize: 8, cellPadding: 3 },
    margin: { left: 10, right: 10 },
    columnStyles: {
      // 🎯 Control de ancho de columnas
      Apodo: { cellWidth: 30 },
      Dispositivo: { cellWidth: 30 },
      Activado: { cellWidth: 15 },
      UltimoRegistro: { cellWidth: 30 },
      ApodoLector: { cellWidth: 30 },
      TieneBateria: { cellWidth: 20 },
      Bateria: { cellWidth: 20 },
      Roll: { cellWidth: 15 },
      Pitch: { cellWidth: 15 },
      Lat: { cellWidth: 20 },
      Lon: { cellWidth: 20 },
    },
    didDrawPage: () => {
      // Número de página
      const pageSize = doc.internal.pageSize;
      const pageHeight = pageSize.height
        ? pageSize.height
        : pageSize.getHeight();
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.text(
        `Página ${doc.internal.getNumberOfPages()}`,
        pageSize.width / 2,
        pageHeight - 5,
        { align: "center" }
      );
    },
  });

  doc.save("status_poc.pdf");
};

const map = ref(null); // Para guardar la instancia del mapa

// Volver a página 1 al cambiar filtros
watch(
  [searchDispositivo, searchApodo, searchActivado, searchBateria, searchTipo],
  () => {
    page.value = 1;
  }
);

watch(detailsDialog, (opened) => {
  if (opened && selectedDevice.value?.Lat && selectedDevice.value?.Lon) {
    setTimeout(() => {
      if (map.value) {
        map.value.remove(); // Limpia el mapa anterior
      }
      map.value = L.map("map", {
        zoomControl: true,
        dragging: true,
        scrollWheelZoom: false,
      }).setView([selectedDevice.value.Lat, selectedDevice.value.Lon], 18);

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "&copy; OpenStreetMap contributors",
      }).addTo(map.value);

      L.marker([selectedDevice.value.Lat, selectedDevice.value.Lon], {
        icon: customIcon,
      })
        .addTo(map.value)
        .bindPopup(
          `<strong>Dispositivo:</strong> ${
            selectedDevice.value.Apodo || "Sin nombre"
          }`
        )
        .openPopup();
    }, 100);
  }
});

// Limpiar filtros
function clearFilters() {
  searchDispositivo.value = "";
  searchApodo.value = "";
  searchActivado.value = "";
  searchBateria.value = "";
  searchTipo.value = "";
  searchPosibleEstado.value = "";
  fechaFin.value = null;
  fechaInicio.value = null;
  itemsPerPage.value = 10;
  page.value = 1;
  snackbarText.value = "Filtros eliminados correctamente";
  snackbar.value = true;
}

// Toggle de Activado/Desactivado
async function toggleActivado(item) {
  try {
    const newStatus = item.Activado === "Activado" ? "Desactivado" : "Activado";
    console.log(
      `Cambiando estado de ${item.Dispositivo} (${item.Apodo}) a ${newStatus}`
    );
    await api.changeDeviceStatus({
      mac_serie: item.Dispositivo, // ← aquí tu MAC
      apodo: item.Apodo, // ← lo tienes en la tabla
      nuevo_estado: newStatus,
      tipo_dispositivo: item.TipoDispositivo, // opcional
      etiqueta: item.Etiqueta, // si la tienes en el dataset
    });
    await reloadData();
    snackbarText.value = `Estado cambiado a ${newStatus}`;
    snackbar.value = true;
  } catch (error) {
    console.error("Error cambiando estado:", error);
    snackbarText.value = error.message || "No se pudo cambiar el estado";
    snackbar.value = true;
  }
}

// Formateo de fecha
function formatDate(value) {
  if (!value || value === "Sin determinar") return "Sin datos";
  const clean = stripOffset(value);
  const d = dayjs(clean);
  return d.isValid() ? d.format("DD/MM/YYYY HH:mm:ss") : "Sin datos";
}

// Formateo batería
function batteryColor(battery) {
  if (battery >= 50) return "green";
  else if (battery >= 25) return "yellow";
  else return "red";
}
function batteryIcon(battery) {
  if (battery >= 50) return "mdi-battery-high";
  else if (battery >= 25) return "mdi-battery-medium";
  else return "mdi-battery-low";
}

// Formateo Roll/Pitch
function formatAngle(value) {
  if (value === null || value === undefined) {
    return "0.00°";
  }
  return parseFloat(value).toFixed(2) + "°";
}

// Editar Apodo
function openEditApodo(item) {
  editedApodo.value = item.Apodo;
  editedDevice.value = item.Dispositivo;
  editDialog.value = true;
}

function openDeviceDetails(item) {
  selectedDevice.value = item;
  detailsDialog.value = true;
}

async function saveApodo() {
  try {
    const dev =
      selectedDevice.value ||
      pocStore.data.find((d) => d.Dispositivo === editedDevice.value);

    await api.changeDeviceApodo({
      mac_serie: editedDevice.value, // MAC
      apodo: editedApodo.value, // nuevo nombre
      estado: dev?.Activado, // “Activado/Desactivado” → tu endpoint lo guarda en "Valor"
      tipo_dispositivo: dev?.TipoDispositivo,
      etiqueta: dev?.Etiqueta,
    });

    await reloadData();
    snackbarText.value = "Apodo actualizado correctamente";
    snackbar.value = true;
    editDialog.value = false;
  } catch (error) {
    console.error("Error actualizando apodo:", error);
    snackbarText.value = error.message || "No se pudo actualizar el apodo";
    snackbar.value = true;
  }
}

onMounted(() => {
  const init = async () => {
    await pocStore.fetchPocData();
    calculateStats();
    //statsDialog.value = true;
  };
  init();
  // Si hay apodo en la query, filtrar automáticamente
  if (route.query.apodo) {
    searchApodo.value = route.query.apodo;
  }
});
</script>

<style scoped>
.search-container {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
  margin: 10px 0;
}
.elevation-1 {
  box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.12), 0px 1px 2px rgba(0, 0, 0, 0.24);
}
</style>
